import React, { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Editor } from "@toast-ui/react-editor";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { db } from "@/firebase/config";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/use-toast";
import { MultiSelect } from "@/components/ui/multi-select";
import { Loader2, Users, Mail, Image as ImageIcon, Bug } from "lucide-react";
import emailjs from "@emailjs/browser";
import {
  getEmailsFromSelectedLists,
  sendBulkEmail,
  getVerifiedEmails,
  getNewsletterSubscribers,
  testFirebaseConnection,
} from "@/lib/emailService";
import { uploadImageToFirebase, processImagesInHtml } from "@/lib/imageUpload";
import { testFirebaseStorage } from "@/lib/storageTest";

import "@toast-ui/editor/dist/toastui-editor.css";
import "./editor.css";

// Email service configuration
const EMAIL_TEMPLATE_ID = "template_y47bxkc";
const EMAIL_SERVICE_ID = "service_3edbwuj";
const EMAIL_PUBLIC_KEY = "pYRLv9sYDpYiLevTX";
const TEST_EMAIL_ADDRESS = "cittasya.website@gmail.com";

export default function CreateNewsletter() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const editorRef = useRef<Editor>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSendingTest, setIsSendingTest] = useState(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [showDebugger, setShowDebugger] = useState(false);
  const [selectedLists, setSelectedLists] = useState<string[]>([
    "verified-users",
    "newsletter-subscribers",
  ]);
  const [subscriberCounts, setSubscriberCounts] = useState({
    verifiedUsers: 0,
    newsletterSubscribers: 0,
  });
  const [isTestingConnection, setIsTestingConnection] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    subject: "",
  });

  // Custom image upload handler for the editor
  const handleImageUpload = async (
    blob: Blob,
    callback: (url: string, altText: string) => void
  ) => {
    try {
      setIsUploadingImage(true);

      // Convert blob to file
      const file = new File([blob], `editor_image_${Date.now()}.png`, {
        type: blob.type || "image/png",
      });

      console.log("🖼️ Uploading image from editor:", {
        size: file.size,
        type: file.type,
        name: file.name,
      });

      // Upload to Firebase Storage
      const firebaseUrl = await uploadImageToFirebase(
        file,
        "newsletter-images"
      );

      // Call the callback with the Firebase URL and alt text
      // The alt text will be used by the editor to create proper image markup
      callback(firebaseUrl, file.name.replace(/\.[^/.]+$/, ""));

      toast({
        title: "Image Uploaded",
        description:
          "Image has been uploaded to Firebase Storage successfully.",
      });

      console.log("✅ Image uploaded successfully:", firebaseUrl);
    } catch (error) {
      console.error("❌ Error uploading image:", error);
      toast({
        variant: "destructive",
        title: "Upload Failed",
        description:
          error instanceof Error ? error.message : "Failed to upload image",
      });
    } finally {
      setIsUploadingImage(false);
    }
  };

  // Test Firebase Storage connection
  const handleTestStorage = async () => {
    setIsTestingConnection(true);
    try {
      const isWorking = await testFirebaseStorage();

      if (isWorking) {
        toast({
          title: "Firebase Storage Test Passed",
          description:
            "Firebase Storage is configured correctly and ready for image uploads.",
        });
      } else {
        toast({
          variant: "destructive",
          title: "Firebase Storage Test Failed",
          description:
            "There was an issue with Firebase Storage configuration.",
        });
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Storage Test Error",
        description:
          error instanceof Error ? error.message : "Unknown storage error",
      });
    } finally {
      setIsTestingConnection(false);
    }
  };

  // Test Firebase connection
  const handleTestConnection = async () => {
    setIsTestingConnection(true);
    try {
      const results = await testFirebaseConnection();
      toast({
        title: "Firebase Test Results",
        description: `Users: ${results.totalUsers}, Verified: ${results.totalVerifiedUsers}, Newsletter: ${results.totalNewsletterSubscribers}`,
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Connection Test Failed",
        description: error instanceof Error ? error.message : "Unknown error",
      });
    } finally {
      setIsTestingConnection(false);
    }
  };

  // Load subscriber counts on component mount
  useEffect(() => {
    // Initialize EmailJS
    console.log("🔧 Initializing EmailJS with public key:", EMAIL_PUBLIC_KEY);
    emailjs.init(EMAIL_PUBLIC_KEY);

    const loadSubscriberCounts = async () => {
      try {
        console.log("🔍 Loading subscriber counts...");

        const [verifiedEmails, newsletterSubs] = await Promise.all([
          getVerifiedEmails(),
          getNewsletterSubscribers(),
        ]);

        console.log("📊 Subscriber Data:", {
          verifiedEmails: verifiedEmails.length,
          verifiedEmailsSample: verifiedEmails.slice(0, 3),
          newsletterSubs: newsletterSubs.length,
          newsletterSubsSample: newsletterSubs.slice(0, 3).map((sub) => ({
            id: sub.id,
            email: sub.email,
          })),
        });

        setSubscriberCounts({
          verifiedUsers: verifiedEmails.length,
          newsletterSubscribers: newsletterSubs.length,
        });
      } catch (error) {
        console.error("Error loading subscriber counts:", error);
        toast({
          variant: "destructive",
          title: "Error",
          description:
            "Failed to load subscriber counts. Please refresh the page.",
        });
      }
    };
    loadSubscriberCounts();
  }, [toast]);

  // Subscriber list options
  const subscriberListOptions = [
    {
      value: "verified-users",
      label: "Verified Users",
      count: subscriberCounts.verifiedUsers,
    },
    {
      value: "newsletter-subscribers",
      label: "Newsletter Subscribers",
      count: subscriberCounts.newsletterSubscribers,
    },
  ];

  const validateForm = () => {
    if (!formData.title || !formData.subject) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Please fill in all required fields",
      });
      return false;
    }

    if (selectedLists.length === 0) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Please select at least one subscriber list",
      });
      return false;
    }

    const contentHtml = editorRef.current?.getInstance().getHTML();

    if (!contentHtml || contentHtml === "<p><br></p>") {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Please add some content to your newsletter",
      });
      return false;
    }

    return true;
  };

  const handleSendTestEmail = async () => {
    if (!validateForm()) {
      return;
    }

    const contentHtml = editorRef.current?.getInstance().getHTML();
    setIsSendingTest(true);

    console.log("🔧 Debug - Email Configuration:", {
      templateId: EMAIL_TEMPLATE_ID,
      serviceId: EMAIL_SERVICE_ID,
      publicKey: EMAIL_PUBLIC_KEY ? "✓ Present" : "✗ Missing",
      testEmail: TEST_EMAIL_ADDRESS,
      subject: formData.subject,
      contentLength: contentHtml?.length,
      selectedLists,
    });

    try {
      // Ensure EmailJS is initialized
      emailjs.init(EMAIL_PUBLIC_KEY);
      console.log("✅ EmailJS re-initialized for test email");

      // Process any remaining base64 images in the content
      console.log("🖼️ Processing images in content...");
      const processedContent = await processImagesInHtml(
        contentHtml!,
        "newsletter-images"
      );

      if (processedContent !== contentHtml) {
        console.log(
          "📝 Images were processed and uploaded to Firebase Storage"
        );
        // Update the editor content with Firebase URLs
        editorRef.current?.getInstance().setHTML(processedContent);
      }

      // Log the final image URLs for debugging
      const parser = new DOMParser();
      const doc = parser.parseFromString(processedContent, "text/html");
      const finalImages = Array.from(doc.querySelectorAll("img")).map(img => ({
        src: img.getAttribute("src"),
        alt: img.getAttribute("alt")
      }));
      console.log("📧 Final Email Images prepared for sending:", finalImages);

      // Test with a simple direct EmailJS call first
      console.log("🧪 Testing direct EmailJS call...");

      const result = await sendBulkEmail(
        EMAIL_TEMPLATE_ID,
        EMAIL_SERVICE_ID,
        EMAIL_PUBLIC_KEY,
        formData.subject,
        processedContent,
        [TEST_EMAIL_ADDRESS],
        formData.title
      );

      console.log("📊 Email Results:", result);

      if (result.successful.length > 0) {
        toast({
          title: "Test Email Sent ✅",
          description: `Test email sent successfully to ${TEST_EMAIL_ADDRESS}. Please check your inbox.`,
        });
        console.log("✅ Test email sent successfully to:", TEST_EMAIL_ADDRESS);
      } else if (result.failed.length > 0) {
        const error = result.failed[0].error;
        console.error("❌ Email failed with error:", error);
        toast({
          variant: "destructive",
          title: "Email Failed",
          description: `Failed to send email: ${error.message}. Please check your EmailJS configuration.`,
        });
      } else {
        throw new Error("No results returned from email service");
      }
    } catch (error) {
      console.error("❌ Failed to send test email:", error);

      // Provide more detailed error information
      let errorMessage = "Failed to send test email. ";
      if (error instanceof Error) {
        errorMessage += error.message;

        // Check for common EmailJS errors
        if (error.message.includes("Public Key")) {
          errorMessage =
            "EmailJS Public Key is invalid or missing. Please check your environment variables.";
        } else if (error.message.includes("Template")) {
          errorMessage =
            "EmailJS Template ID is invalid. Please verify your template configuration.";
        } else if (error.message.includes("Service")) {
          errorMessage =
            "EmailJS Service ID is invalid. Please verify your service configuration.";
        }
      } else {
        errorMessage += "Unknown error occurred.";
      }

      toast({
        variant: "destructive",
        title: "Test Email Error",
        description: errorMessage,
      });
    } finally {
      setIsSendingTest(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const contentHtml = editorRef.current?.getInstance().getHTML();

    setIsSubmitting(true);

    try {
      // Ensure EmailJS is initialized
      emailjs.init(EMAIL_PUBLIC_KEY);
      console.log("✅ EmailJS initialized for newsletter sending");

      // Process any remaining base64 images in the content
      console.log("🖼️ Processing images before submission...");
      const processedContent = await processImagesInHtml(
        contentHtml!,
        "newsletter-images"
      );

      if (processedContent !== contentHtml) {
        console.log(
          "📝 Images were processed and uploaded to Firebase Storage"
        );
        // Update the editor content with Firebase URLs for future reference
        editorRef.current?.getInstance().setHTML(processedContent);
      }

      // Save newsletter to Firebase with processed content
      await addDoc(collection(db, "NewslettersWeb"), {
        ...formData,
        contentHtml: processedContent,
        selectedLists,
        createdAt: serverTimestamp(),
      });

      // Get emails from selected lists
      const selectedEmails = await getEmailsFromSelectedLists(selectedLists);

      if (selectedEmails.length === 0) {
        toast({
          title: "Newsletter Created",
          description:
            "Newsletter saved but no subscribers found in selected lists.",
        });
        navigate("/admin/newsletters");
        return;
      }

      console.log(
        `📧 Sending newsletter to ${selectedEmails.length} unique subscribers from selected lists:`,
        selectedLists
      );

      // Send newsletter to selected subscribers with processed content
      const emailResults = await sendBulkEmail(
        EMAIL_TEMPLATE_ID,
        EMAIL_SERVICE_ID,
        EMAIL_PUBLIC_KEY,
        formData.subject,
        processedContent,
        selectedEmails,
        formData.title
      );

      // Show results
      const successCount = emailResults.successful.length;
      const failureCount = emailResults.failed.length;

      toast({
        title: "Newsletter Sent Successfully! 🎉",
        description: `Newsletter sent to ${successCount} recipients from selected lists. ${
          failureCount > 0
            ? `Failed to send to ${failureCount} recipients.`
            : ""
        }`,
      });

      navigate("/admin/newsletters");
    } catch (error) {
      console.error("❌ Error creating/sending newsletter:", error);

      let errorMessage = "Failed to create or send newsletter. ";
      if (error instanceof Error) {
        errorMessage += error.message;
      }

      toast({
        variant: "destructive",
        title: "Newsletter Error",
        description: errorMessage,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-forest-green/10 flex items-center justify-center">
            <Mail className="h-5 w-5 text-forest-green" />
          </div>
          <h1 className="text-3xl font-heading text-forest-green">
            Create Newsletter
          </h1>
        </div>

        {/* Debug buttons */}
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => setShowDebugger(!showDebugger)}
            className="border-soft-saffron text-soft-saffron hover:bg-soft-saffron/10"
          >
            <Bug className="mr-2 h-4 w-4" />
            {showDebugger ? "Hide" : "Show"} Debugger
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={handleTestStorage}
            disabled={isTestingConnection}
            className="border-soft-saffron text-soft-saffron hover:bg-soft-saffron/10"
          >
            {isTestingConnection && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            <ImageIcon className="mr-2 h-4 w-4" />
            Test Storage
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={handleTestConnection}
            disabled={isTestingConnection}
            className="border-forest-green text-forest-green hover:bg-forest-green/10"
          >
            {isTestingConnection && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            Test Firebase
          </Button>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Subscriber Lists */}
          <div className="lg:col-span-1 space-y-4">
            <div className="bg-white rounded-lg border border-misty-sage p-4">
              <div className="space-y-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Users className="h-5 w-5 text-forest-green" />
                    <Label className="text-forest-green font-medium">
                      Select Subscriber Lists
                    </Label>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      // Force refresh subscriber counts
                      const loadCounts = async () => {
                        try {
                          const [verifiedEmails, newsletterSubs] =
                            await Promise.all([
                              getVerifiedEmails(),
                              getNewsletterSubscribers(),
                            ]);
                          setSubscriberCounts({
                            verifiedUsers: verifiedEmails.length,
                            newsletterSubscribers: newsletterSubs.length,
                          });
                          toast({
                            title: "Counts Refreshed",
                            description: "Subscriber counts have been updated",
                          });
                        } catch (error) {
                          console.error("Error refreshing counts:", error);
                        }
                      };
                      loadCounts();
                    }}
                    className="text-forest-green/70 hover:text-forest-green"
                  >
                    Refresh
                  </Button>
                </div>

                <MultiSelect
                  options={subscriberListOptions}
                  selected={selectedLists}
                  onChange={setSelectedLists}
                  placeholder="Choose subscriber lists..."
                  className="w-full"
                />

                {selectedLists.length > 0 && (
                  <div className="mt-3 p-3 bg-forest-green/5 rounded-md">
                    <p className="text-sm text-forest-green/80 font-medium mb-2">
                      Selected Lists:
                    </p>
                    {selectedLists.map((listId) => {
                      const option = subscriberListOptions.find(
                        (opt) => opt.value === listId
                      );
                      return (
                        <div
                          key={listId}
                          className="text-sm text-forest-green/70"
                        >
                          • {option?.label} ({option?.count || 0} subscribers)
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Newsletter Content */}
          <div className="lg:col-span-2 space-y-6">
            <div className="space-y-2">
              <Label htmlFor="title" className="text-forest-green">
                Title
              </Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, title: e.target.value }))
                }
                placeholder="Newsletter Title"
                required
                className="border-misty-sage text-forest-green placeholder:text-forest-green/50 focus-visible:ring-forest-green"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="subject" className="text-forest-green">
                Subject
              </Label>
              <Input
                id="subject"
                value={formData.subject}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, subject: e.target.value }))
                }
                placeholder="Newsletter Subject"
                required
                className="border-misty-sage text-forest-green placeholder:text-forest-green/50 focus-visible:ring-forest-green"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-forest-green">Content</Label>
                {isUploadingImage && (
                  <div className="flex items-center gap-2 text-sm text-forest-green/70">
                    <ImageIcon className="w-4 h-4 animate-pulse" />
                    <span>Uploading image...</span>
                  </div>
                )}
              </div>
              <div className="border border-misty-sage rounded-md">
                <Editor
                  ref={editorRef}
                  initialValue=""
                  height="500px"
                  initialEditType="wysiwyg"
                  useCommandShortcut={true}
                  hideModeSwitch={false}
                  theme="cittasya"
                  hooks={{
                    addImageBlobHook: handleImageUpload,
                  }}
                  toolbarItems={[
                    ["heading", "bold", "italic", "strike"],
                    ["hr", "quote"],
                    ["ul", "ol", "task", "indent", "outdent"],
                    ["table", "image", "link"],
                    ["code", "codeblock"],
                    ["scrollSync"],
                  ]}
                  customHTMLRenderer={{
                    image(node) {
                      return [
                        {
                          type: "openTag",
                          tagName: "img",
                          attributes: {
                            src: node.destination || "",
                            alt:
                              node.firstChild?.literal ||
                              node.destination
                                ?.split("/")
                                .pop()
                                ?.split("_")
                                .slice(1)
                                .join("_")
                                .replace(/\.[^/.]+$/, "") ||
                              "image",
                          },
                        },
                        { type: "closeTag", tagName: "img" },
                      ];
                    },
                  }}
                />
              </div>
              <p className="text-xs text-forest-green/60 mt-2">
                💡 Images will be automatically uploaded to Firebase Storage
                when you add them to the editor. You can switch between WYSIWYG
                and Markdown modes to add image descriptions.
              </p>
            </div>
          </div>
        </div>

        <div className="flex justify-end space-x-4 pt-6 border-t border-misty-sage">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate("/admin/newsletters")}
            className="border-misty-sage text-forest-green hover:bg-forest-green/5"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSendTestEmail}
            disabled={isSendingTest || isSubmitting}
            variant="outline"
            className="border-forest-green text-forest-green hover:bg-forest-green/10"
          >
            {isSendingTest && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isSendingTest ? "Sending Test..." : "Send Test Email"}
          </Button>
          <Button
            type="submit"
            disabled={
              isSubmitting || isSendingTest || selectedLists.length === 0
            }
            className="bg-forest-green hover:bg-forest-green/90"
          >
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isSubmitting
              ? "Sending Newsletter..."
              : "Create & Send Newsletter"}
          </Button>
        </div>
      </form>
    </div>
  );
}
